import { useAIAvailability } from "@/lib/aiAvailability";
import { AIText } from "@/components/AIText";
import { useState, useRef, useEffect } from "react";
import { API } from "@/lib/api";
import { useFloatingPosition, panelPlacement } from "@/lib/floatingPosition";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, X, Loader2, Sparkles } from "lucide-react";

const BUTTON_SIZE = 56; // h-14 w-14
const POSITION_KEY = "almox_ai_button_pos";

const SUGGESTIONS = [
  "Quais peças estão críticas ou sem estoque?",
  "Onde está a resistência do cabeçote?",
  "Quais peças da Extrusora 160 preciso repor?",
];

export function AIChat() {
  const ai = useAIAvailability();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const floating = useFloatingPosition(POSITION_KEY, BUTTON_SIZE);
  const buttonStyle = floating.pos ? { left: floating.pos.x, top: floating.pos.y, right: "auto", bottom: "auto" } : undefined;
  const panelStyle = floating.pos ? (() => {
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const panel = { width: Math.min(384, viewport.width * 0.92), height: Math.min(560, viewport.height * 0.7) };
    const place = panelPlacement(floating.pos, BUTTON_SIZE, panel, viewport);
    return { ...place, right: "auto", bottom: "auto" };
  })() : undefined;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || busy || !ai.available) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const token = localStorage.getItem("almox_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (res.status === 503) {
        const error = await res.json().catch(() => null);
        if (error?.detail?.code === "AI_TEMPORARILY_UNAVAILABLE") {
          ai.markUnavailable();
          setMessages((m) => m.slice(0, -2));
          return;
        }
      }
      if (!res.ok || !res.body) {
        // Limite mensal (429) e erros do provedor trazem uma mensagem pronta do servidor.
        const error = res.ok ? null : await res.json().catch(() => null);
        const content = error?.detail?.message || "Não consegui responder agora. Verifique sua sessão e tente novamente.";
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content }; return c; });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          let payload;
          try { payload = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (payload.delta) {
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + payload.delta }; return c; });
          } else if (payload.error) {
            const content = typeof payload.error === "string" ? payload.error : "Não consegui responder agora. Tente novamente.";
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content }; return c; });
          }
        }
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Falha ao conectar ao assistente." }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        {...floating.handlers}
        onClick={() => { if (!floating.consumeDrag()) setOpen((o) => !o); }}
        style={buttonStyle}
        className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-sky-600 hover:bg-sky-500 text-white shadow-lg shadow-sky-900/50 flex items-center justify-center transition-transform active:scale-95 cursor-grab active:cursor-grabbing touch-none select-none"
        title="Assistente IA (arraste para mover)"
        aria-label="Assistente IA"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>

      {open && (
        <div data-testid="ai-chat-panel" style={panelStyle} className="fixed bottom-24 right-5 z-40 w-[92vw] max-w-sm h-[70vh] max-h-[560px] rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 bg-slate-950/60">
            <Sparkles className="h-5 w-5 text-sky-400" />
            <div className="flex-1"><div className="font-display font-bold text-slate-100 leading-tight">Assistente Peça360</div><div className="text-[11px] text-slate-500">IA · consultas do almoxarifado</div></div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {!ai.available && <p role="status" data-testid="ai-unavailable" className="text-sm text-amber-200">{ai.message}</p>}
            {ai.available && messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-slate-400">Pergunte sobre peças, estoque, localização ou reposição.</p>
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="ai-chat-suggestion" onClick={() => send(s)} className="block w-full text-left text-sm rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2 text-sky-200 hover:bg-slate-800/50">{s}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid="ai-chat-message" className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "whitespace-pre-wrap bg-sky-700 text-white" : "bg-slate-800 text-slate-100"}`}>
                  {m.content
                    ? (m.role === "assistant" ? <AIText text={m.content} /> : m.content)
                    : (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-slate-800 flex items-center gap-2">
            <Input
              data-testid="ai-chat-input"
              disabled={!ai.available || busy}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Digite sua pergunta..."
              className="bg-slate-950 border-slate-700 h-11"
            />
            <Button data-testid="ai-chat-send" onClick={() => send()} disabled={!ai.available || busy || !input.trim()} className="h-11 w-11 p-0 bg-sky-600 hover:bg-sky-500 shrink-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
